import type { GridItem } from '@/features/dashboard/lib/grid-math'
import type { LinkingColor } from '@/state/linking'

// Single breakpoint — studio only (full width always)
export const GRID_BREAKPOINTS = {
  studio: 0,
}

export const GRID_COLS = {
  studio: 32,
  laptop: 28,
  compact: 24,
}

export const LAYOUT_BREAKPOINT_LAPTOP = 1600
export const LAYOUT_BREAKPOINT_COMPACT = 1200
export const DASHBOARD_DESKTOP_MIN_WIDTH = LAYOUT_BREAKPOINT_COMPACT
export const DASHBOARD_MOBILE_LAYOUT_BREAKPOINT = 1024
export const DASHBOARD_LAYOUT_DENSITY = 1
export const DASHBOARD_DESKTOP_VISUAL_SCALE = 0.9
// Mobile trade-page header height (see exchange-tab-group.tsx) — shared with anything
// that needs to size itself against the remaining viewport below it (e.g.
// screener-page.tsx's phone layout).
export const TRADE_HEADER_HEIGHT_PX = 68
// Desktop shell chrome around a trade page's content, in screen px: the 44px top bar, 8px
// above the content, an 8px gap above the status bar and the 32px status bar on the window's
// bottom edge (exchange-tab-group.tsx).
export const TRADE_DESKTOP_CHROME_PX = 44 + 8 + 8 + 32
// The same chrome on a full-page route (Home, Risk Slides, Hedge, Screener, Arbitrage, Assistant,
// /tx): the status bar only shows on dashboard pages (exchange-tab-group.tsx), so there's no 32px
// bar — such a page sizes itself to `100dvh - TRADE_DESKTOP_PAGE_CHROME_PX` (divided by the
// visual scale).
export const TRADE_DESKTOP_PAGE_CHROME_PX = 44 + 8 + 8

export function getDashboardLayoutDensity(isDashboardMobile: boolean): number {
  return isDashboardMobile ? 1 : DASHBOARD_LAYOUT_DENSITY
}

export function getDashboardVisualScale(isDashboardMobile: boolean): number {
  return isDashboardMobile ? 1 : DASHBOARD_DESKTOP_VISUAL_SCALE
}

export function getDisplayCols(containerWidth: number): number {
  if (containerWidth > 0 && containerWidth < LAYOUT_BREAKPOINT_COMPACT) {
    return GRID_COLS.compact
  }
  if (containerWidth > 0 && containerWidth < LAYOUT_BREAKPOINT_LAPTOP) {
    return GRID_COLS.laptop
  }
  return GRID_COLS.studio
}

export const GRID_MARGIN: [number, number] = [8, 8]
export const GRID_CONTAINER_PADDING: [number, number] = [8, 0]
export const GRID_ROWS = 24
export const GRID_ROW_HEIGHT_MIN = 36

export const DASHBOARD_HEADER_HEIGHT = 44
// Phones: the full-width status bar fixed to the screen bottom (--status-bar-height).
export const DASHBOARD_BOTTOM_STATUS_HEIGHT = 40
// Desktop: the 32px status bar along the window's bottom edge (exchange-tab-group.tsx).
export const DASHBOARD_DESKTOP_STATUS_HEIGHT = 32
export const DASHBOARD_GRID_EDGE_GAP = 8
// Screen px kept free under the grid: the status bar and one grid gap above it.
export const DASHBOARD_BOTTOM_RESERVED_HEIGHT =
  DASHBOARD_BOTTOM_STATUS_HEIGHT + DASHBOARD_GRID_EDGE_GAP
export const DASHBOARD_DESKTOP_BOTTOM_RESERVED_HEIGHT =
  DASHBOARD_DESKTOP_STATUS_HEIGHT + DASHBOARD_GRID_EDGE_GAP

export function getDashboardBottomReserve(isDashboardMobile: boolean): number {
  return isDashboardMobile
    ? DASHBOARD_BOTTOM_RESERVED_HEIGHT
    : DASHBOARD_DESKTOP_BOTTOM_RESERVED_HEIGHT
}
export const DASHBOARD_INTERACTION_WORKSPACE_HEIGHT = 240

// Studio Mode Layout: 32 cols × 24 rows
export const studioLayout: GridItem[] = [
  { i: 'watchlist', x: 0, y: 0, w: 5, h: 13 },
  { i: 'account-cards', x: 0, y: 13, w: 5, h: 5 },
  { i: 'newsfeed', x: 0, y: 18, w: 11, h: 6 },
  { i: 'price-chart', x: 5, y: 0, w: 16, h: 13 },
  { i: 'positions', x: 5, y: 13, w: 16, h: 5 },
  { i: 'economic-calendar', x: 11, y: 18, w: 10, h: 6 },
  { i: 'chat-order-form', x: 21, y: 0, w: 11, h: 24 },
]

export const gridLayouts: { studio: GridItem[] } = {
  studio: studioLayout,
}

/**
 * A GridItem plus the template-only fields needed to seed a widget's initial
 * linking-group color and per-widget config on template apply. Kept separate
 * from the shared GridItem (grid-math.ts) used by drag/resize elsewhere.
 */
export interface TemplateGridItem extends GridItem {
  group?: LinkingColor
  config?: Record<string, string>
}

export const TEMPLATE_LAYOUTS: Record<string, TemplateGridItem[]> = {
  'empty-template': [],

  // The beginner page the welcome tour offers to set up: only the five widgets needed to pick
  // an instrument, read its chart, place an order and see the result. One linking group (mint)
  // so the watchlist drives the chart and the order form; account-cards stays unlinked (it
  // renders UnlinkedTile regardless of group, see the trading template below).
  starter: [
    { i: 'watchlist', x: 0, y: 0, w: 6, h: 14, group: 'mint' },
    { i: 'price-chart', x: 6, y: 0, w: 16, h: 14, group: 'mint' },
    { i: 'chat-order-form', x: 22, y: 0, w: 10, h: 24, group: 'mint' },
    { i: 'account-cards', x: 0, y: 14, w: 6, h: 10, group: 'unlinked' },
    { i: 'positions', x: 6, y: 14, w: 16, h: 10, group: 'mint' },
  ],
  // Single-symbol trading workstation: watchlist -> chart -> positions -> order
  // form, plus quick execution helpers (one-tap-buy, quick-order) below the
  // chart/positions column. Deliberately one linking group throughout (mint) —
  // the whole page tracks one instrument end to end. account-cards/
  // economic-calendar/newsfeed render UnlinkedTile regardless of group
  // (account-cards.tsx, newsfeed.tsx, economic-calendar.tsx), so they're left
  // unlinked rather than given a group with no effect.
  trading: [
    { i: 'watchlist', x: 0, y: 0, w: 5, h: 12, group: 'mint' },
    { i: 'account-cards', x: 0, y: 12, w: 5, h: 6, group: 'unlinked' },
    { i: 'price-chart', x: 5, y: 0, w: 16, h: 12, group: 'mint' },
    { i: 'positions', x: 5, y: 12, w: 16, h: 6, group: 'mint' },
    { i: 'chat-order-form', x: 21, y: 0, w: 11, h: 24, group: 'mint' },
    { i: 'one-tap-buy', x: 0, y: 18, w: 6, h: 6, group: 'mint' },
    { i: 'quick-order', x: 6, y: 18, w: 8, h: 12, group: 'mint' },
    { i: 'economic-calendar', x: 14, y: 18, w: 7, h: 6, group: 'unlinked' },
    { i: 'newsfeed', x: 14, y: 24, w: 7, h: 6, group: 'unlinked' },
  ],

  // 4 independent symbol panels: data + price-chart pairs, each its own linking
  // group so they don't silently share one watchlist symbol. Each data widget
  // gets a distinct analyticsType so the 4 panels show different content, not
  // 4 copies of the same default metric.
  'spot-futures-metrics': [
    {
      i: 'data',
      x: 0,
      y: 0,
      w: 8,
      h: 12,
      group: 'mint',
      config: { kind: 'amberdata', analyticsType: '1' }, // VOL_METRICS_24HR
    },
    { i: 'price-chart', x: 8, y: 0, w: 8, h: 12, group: 'mint' },
    {
      i: 'data',
      x: 16,
      y: 0,
      w: 8,
      h: 12,
      group: 'yellow',
      config: { kind: 'amberdata', analyticsType: '2' }, // PUT_CALL_RATIO
    },
    { i: 'price-chart', x: 24, y: 0, w: 8, h: 12, group: 'yellow' },
    {
      i: 'data',
      x: 0,
      y: 12,
      w: 8,
      h: 12,
      group: 'orange',
      config: { kind: 'amberdata', analyticsType: '6' }, // TERM_STRUCTURE_CONSTANT
    },
    { i: 'price-chart', x: 8, y: 12, w: 8, h: 12, group: 'orange' },
    {
      i: 'data',
      x: 16,
      y: 12,
      w: 8,
      h: 12,
      group: 'purple',
      config: { kind: 'amberdata', analyticsType: '12' }, // USD_GAMMA
    },
    { i: 'price-chart', x: 24, y: 12, w: 8, h: 12, group: 'purple' },
  ],

  // 2 × 4 grid of independent price charts — already correctly split across 8
  // distinct linking groups (one per tile). Timeframe intentionally left at
  // the shared default: varying both symbol and timeframe at once would make
  // the 8 tiles impossible to compare against each other.
  'chart-spotlight': [
    { i: 'price-chart', x: 0, y: 0, w: 8, h: 12, group: 'mint' },
    { i: 'price-chart', x: 8, y: 0, w: 8, h: 12, group: 'yellow' },
    { i: 'price-chart', x: 16, y: 0, w: 8, h: 12, group: 'orange' },
    { i: 'price-chart', x: 24, y: 0, w: 8, h: 12, group: 'purple' },
    { i: 'price-chart', x: 0, y: 12, w: 8, h: 12, group: 'blue' },
    { i: 'price-chart', x: 8, y: 12, w: 8, h: 12, group: 'pink' },
    { i: 'price-chart', x: 16, y: 12, w: 8, h: 12, group: 'green' },
    { i: 'price-chart', x: 24, y: 12, w: 8, h: 12, group: 'unlinked' },
  ],

  // Single-asset options trading dashboard — every widget shares one linking
  // group (mint) and one underlying (BTC) rather than splitting across two
  // unrelated symbols: main chart, order book, an options-analytics data
  // panel, a spreader matrix, and the order form, all for the same asset.
  // order-book and spreader both read their symbol straight from
  // useLinkingGroupState (orderbook.tsx, spreader-matrix-widget.tsx), so they
  // must share the group rather than being unlinked, or they start empty.
  'options-trading': [
    { i: 'price-chart', x: 0, y: 0, w: 12, h: 16, group: 'mint' },
    { i: 'order-book', x: 12, y: 0, w: 10, h: 8, group: 'mint' },
    {
      i: 'data',
      x: 12,
      y: 8,
      w: 10,
      h: 8,
      group: 'mint',
      config: { kind: 'optionVolume', optionUnderlying: 'BTC' },
    },
    { i: 'spreader', x: 0, y: 16, w: 22, h: 8, group: 'mint' },
    { i: 'chat-order-form', x: 22, y: 0, w: 10, h: 24, group: 'mint' },
  ],

  // 5-panel options analytics dashboard: left column + center chart = asset A
  // (BTC, mint), right column = asset B (ETH, yellow). Each data widget gets a
  // distinct option-analytics kind so the 4 panels are genuinely different
  // views, not 4 copies of the same default metric.
  'options-metrics': [
    {
      i: 'data',
      x: 0,
      y: 0,
      w: 8,
      h: 12,
      group: 'mint',
      config: { kind: 'optionVolume', optionUnderlying: 'BTC' },
    },
    {
      i: 'data',
      x: 0,
      y: 12,
      w: 8,
      h: 12,
      group: 'mint',
      config: { kind: 'optionOpenInterest', optionUnderlying: 'BTC' },
    },
    { i: 'price-chart', x: 8, y: 0, w: 16, h: 24, group: 'mint' },
    {
      i: 'data',
      x: 24,
      y: 0,
      w: 8,
      h: 12,
      group: 'yellow',
      config: { kind: 'optionStrikeFlow', optionUnderlying: 'ETH' },
    },
    {
      i: 'data',
      x: 24,
      y: 12,
      w: 8,
      h: 12,
      group: 'yellow',
      config: { kind: 'optionStructure', optionUnderlying: 'ETH' },
    },
  ],

  // Portfolio-wide risk dashboard: risk-slides and positions are all-positions
  // views, not single-symbol, so they intentionally stay unlinked — do not
  // "fix" this back to a shared group, it's correct as-is. Only the data panel
  // is symbol-scoped; seeded with GEX (gamma exposure), a risk-relevant metric
  // rather than the generic default.
  'risk-slides': [
    { i: 'risk-slides', x: 0, y: 0, w: 14, h: 24, group: 'unlinked' },
    { i: 'positions', x: 14, y: 0, w: 18, h: 12, group: 'unlinked' },
    {
      i: 'data',
      x: 14,
      y: 12,
      w: 18,
      h: 12,
      group: 'mint',
      config: { kind: 'amberdata', analyticsType: '13' }, // GEX
    },
  ],

  // account-cards is account-scoped (no app-wide "default account" concept,
  // left unlinked/unset). Two data panels split across colors/metrics so they
  // don't look duplicated. Right column (chat-order-form + ticker) is the
  // active trading pair; preset-manager sits under it for order-preset
  // shortcuts (account/preset-scoped, not symbol-linked).
  'positions-management': [
    { i: 'account-cards', x: 0, y: 0, w: 8, h: 10, group: 'unlinked' },
    {
      i: 'data',
      x: 0,
      y: 10,
      w: 8,
      h: 14,
      group: 'yellow',
      config: { kind: 'amberdata', analyticsType: '2' }, // PUT_CALL_RATIO
    },
    {
      i: 'data',
      x: 8,
      y: 0,
      w: 14,
      h: 24,
      group: 'mint',
      config: { kind: 'amberdata', analyticsType: '1' }, // VOL_METRICS_24HR
    },
    { i: 'chat-order-form', x: 22, y: 0, w: 10, h: 12, group: 'mint' },
    { i: 'ticker', x: 22, y: 12, w: 10, h: 7, group: 'mint' },
    { i: 'preset-manager', x: 22, y: 19, w: 10, h: 5, group: 'unlinked' },
  ],
}

/**
 * Find the first empty position in the grid for a widget of size (w × h).
 * Scans row by row, left to right, preferring to fill existing rows before
 * adding a new row at the bottom.
 *
 * If minW < w and no full-width position fits within existing rows, retries
 * with minW so narrow gaps can be filled instead of always appending a new row.
 * Returns the effective width used alongside the position.
 *
 * Performance optimized using bitsets (BigInt) to handle occupancy checks.
 */
export function findEmptyPosition(
  layout: GridItem[],
  w: number,
  h: number,
  cols: number = GRID_COLS.studio,
  minW: number = w
): { x: number; y: number; w: number } {
  if (layout.length === 0) return { x: 0, y: 0, w }

  let maxY = 0
  for (let i = 0; i < layout.length; i++) {
    const bottom = layout[i].y + layout[i].h
    if (bottom > maxY) maxY = bottom
  }

  const gridRows = Math.max(GRID_ROWS, maxY)
  // Use a bitset (BigInt) per row to track occupancy.
  // BigUint64Array safely handles up to 64 columns.
  const grid = new BigUint64Array(gridRows)

  for (let i = 0; i < layout.length; i++) {
    const item = layout[i]
    // Create a bitmask for the item's width at its x position.
    const itemMask = ((1n << BigInt(item.w)) - 1n) << BigInt(item.x)
    const rowEnd = Math.min(item.y + item.h, gridRows)
    for (let row = item.y; row < rowEnd; row++) {
      grid[row] |= itemMask
    }
  }

  // Full scan: widget must fit entirely within the given maxRow bound.
  const scanFull = (targetW: number, maxRow: number): { x: number; y: number } | null => {
    if (maxRow < 0) return null
    const widgetMask = (1n << BigInt(targetW)) - 1n
    const maxX = cols - targetW

    for (let y = 0; y <= maxRow; y++) {
      let combinedMask = 0n
      for (let row = y; row < y + h; row++) {
        combinedMask |= grid[row]
      }
      for (let x = 0; x <= maxX; x++) {
        if ((combinedMask & (widgetMask << BigInt(x))) === 0n) {
          return { x, y }
        }
      }
    }
    return null
  }

  // Partial scan for minW: only checks rows that have actual widgets (rows within gridRows).
  // Allows the widget to extend below existing content — only x-conflicts matter.
  const scanMinWPartial = (): { x: number; y: number } | null => {
    const widgetMask = (1n << BigInt(minW)) - 1n
    const maxX = cols - minW

    for (let y = 0; y < maxY; y++) {
      let combinedMask = 0n
      // Only check rows that exist in grid; rows beyond gridRows are empty.
      const rowEnd = Math.min(y + h, gridRows)
      for (let row = y; row < rowEnd; row++) {
        combinedMask |= grid[row]
      }
      for (let x = 0; x <= maxX; x++) {
        if ((combinedMask & (widgetMask << BigInt(x))) === 0n) {
          return { x, y }
        }
      }
    }
    return null
  }

  // Priority 1: full width within existing rows (y + h <= maxY)
  const existingFullPos = scanFull(w, maxY - h)
  if (existingFullPos) return { ...existingFullPos, w }

  // Priority 2: minW within existing rows (partial — widget may extend below)
  if (minW < w) {
    const minPos = scanMinWPartial()
    if (minPos) {
      // Re-check occupancy at this position and grow width only as far as columns are free.
      // scanMinWPartial verified minW columns clear; naively using cols-x can assign a
      // wider widget that overlaps whatever sits to the right.
      let combinedMask = 0n
      const rowEnd = Math.min(minPos.y + h, gridRows)
      for (let row = minPos.y; row < rowEnd; row++) {
        combinedMask |= grid[row]
      }
      let effectiveW = minW
      for (let tryW = minW + 1; tryW <= Math.min(w, cols - minPos.x); tryW++) {
        const mask = ((1n << BigInt(tryW)) - 1n) << BigInt(minPos.x)
        if ((combinedMask & mask) !== 0n) break
        effectiveW = tryW
      }
      return { ...minPos, w: effectiveW }
    }
  }

  // Priority 3: full width in empty rows within the default grid height
  const emptyFullPos = scanFull(w, gridRows - h)
  if (emptyFullPos) return { ...emptyFullPos, w }

  // No space in grid — append below all existing items
  return { x: 0, y: maxY, w }
}
